import { Vector3 } from '@technology/web3d';

/**
 * Calculates the scale factor for an object to maintain consistent apparent size based on camera distance.
 *
 * Implements size attenuation by scaling the object based on camera distance:
 * - Far zone (> farBoundary): Scale UP to compensate for distance
 * - Near zone (< nearBoundary): Scale DOWN to compensate for closeness
 * - Comfort zone (nearBoundary to farBoundary): Use base size
 *
 * This function is used for map station markers, frustum indicators, and other objects
 * that need to maintain consistent visual size across different camera distances.
 *
 * @param cameraPosition - World position of the camera
 * @param objectPosition - World position of the object to scale
 * @param baseSize - Base scale size of the object (used within comfort zone)
 * @param nearBoundary - Distance threshold for close scaling (scales down when closer), optional
 * @param farBoundary - Distance threshold for far scaling (scales up when farther), optional
 * @returns Calculated scale factor to apply to the object
 */
export function calculateDistanceBasedScale(
  cameraPosition: Vector3,
  objectPosition: Vector3,
  baseSize: number,
  nearBoundary?: number,
  farBoundary?: number
): number {
  const distance = cameraPosition.distanceTo(objectPosition);

  let scale;

  if (farBoundary && distance > farBoundary) {
    // Far zone: Scale UP proportionally to compensate for distance
    // As distance increases, scale increases to maintain apparent size
    scale = baseSize * (distance / farBoundary);
  } else if (nearBoundary && distance < nearBoundary) {
    // Near zone: Scale DOWN proportionally to compensate for closeness
    // As distance decreases, scale decreases to maintain apparent size
    scale = baseSize * (distance / nearBoundary);
  } else {
    // Comfort zone (nearBoundary ≤ distance ≤ farBoundary):
    // No size attenuation applied, point appears at intended base size
    scale = baseSize;
  }

  return scale;
}
